import { Boss, type BossDef } from './boss-base';
import { SENTINEL } from './sentinel';
import { CARRIER, CarrierBoss } from './carrier';
import { HIVE, HiveBoss } from './hive';
import { SERPENT, SerpentBoss } from './serpent';
import { KRAKEN, KrakenBoss } from './kraken';
import { CENTIPEDE, CentipedeBoss } from './centipede';
import { QUARTET, QuartetBoss } from './quartet';
import { PYRE, PyreBoss } from './pyre';
import { TIDE, TideBoss } from './tide';
import { PRISM, PrismBoss } from './prism';
import { NULL_CROWN, NullBoss } from './null-crown';
import { TERRA, TerraBoss } from './terra';
import { GALE, GaleBoss } from './gale';
import { AVATAR, AvatarBoss } from './avatar';
import { MIRROR, MirrorBoss } from './mirror';
import { SPLITTER, SplitterBoss } from './splitter';
import { ADMIRAL, AdmiralBoss } from './admiral';
import { CHRONOS, ChronosBoss } from './chronos';
import { MAGNET, MagnetBoss } from './magnet';
import { BASTION, BastionBoss } from './bastion';
import { STALKER, StalkerBoss } from './stalker';
import { SINGULARITY, SingularityBoss } from './singularity';
import { BROODWORM, BroodwormBoss } from './broodworm';
import { WEBSPINNER, WebspinnerBoss } from './webspinner';
import { WARDEN, WardenBoss } from './warden';
import { sectorAt } from '@/config/sectors';
import { BossRotation, sectorRules, type RotationEntry } from './rotation';

export interface BossEntry {
  def: BossDef;
  create(level: number, x: number, y: number, homeX: number, homeY: number): Boss;
}

/** All bosses available to the run (25 — docs/02). */
export const BOSS_POOL: ReadonlyArray<BossEntry> = [
  {
    def: SENTINEL,
    create: (level, x, y, hx, hy) => new Boss(SENTINEL, level, x, y, hx, hy),
  },
  {
    def: CARRIER,
    create: (level, x, y, hx, hy) => new CarrierBoss(level, x, y, hx, hy),
  },
  {
    def: HIVE,
    create: (level, x, y, hx, hy) => new HiveBoss(level, x, y, hx, hy),
  },
  {
    def: SERPENT,
    create: (level, x, y, hx, hy) => new SerpentBoss(level, x, y, hx, hy),
  },
  {
    def: KRAKEN,
    create: (level, x, y, hx, hy) => new KrakenBoss(level, x, y, hx, hy),
  },
  {
    def: CENTIPEDE,
    create: (level, x, y, hx, hy) => new CentipedeBoss(level, x, y, hx, hy),
  },
  {
    def: QUARTET,
    create: (level, x, y, hx, hy) => new QuartetBoss(level, x, y, hx, hy),
  },
  {
    def: PYRE,
    create: (level, x, y, hx, hy) => new PyreBoss(level, x, y, hx, hy),
  },
  {
    def: TIDE,
    create: (level, x, y, hx, hy) => new TideBoss(level, x, y, hx, hy),
  },
  {
    def: PRISM,
    create: (level, x, y, hx, hy) => new PrismBoss(level, x, y, hx, hy),
  },
  {
    def: NULL_CROWN,
    create: (level, x, y, hx, hy) => new NullBoss(level, x, y, hx, hy),
  },
  {
    def: TERRA,
    create: (level, x, y, hx, hy) => new TerraBoss(level, x, y, hx, hy),
  },
  {
    def: GALE,
    create: (level, x, y, hx, hy) => new GaleBoss(level, x, y, hx, hy),
  },
  {
    def: AVATAR,
    create: (level, x, y, hx, hy) => new AvatarBoss(level, x, y, hx, hy),
  },
  {
    def: MIRROR,
    create: (level, x, y, hx, hy) => new MirrorBoss(level, x, y, hx, hy),
  },
  {
    def: SPLITTER,
    create: (level, x, y, hx, hy) => new SplitterBoss(level, x, y, hx, hy),
  },
  {
    def: ADMIRAL,
    create: (level, x, y, hx, hy) => new AdmiralBoss(level, x, y, hx, hy),
  },
  {
    def: CHRONOS,
    create: (level, x, y, hx, hy) => new ChronosBoss(level, x, y, hx, hy),
  },
  {
    def: MAGNET,
    create: (level, x, y, hx, hy) => new MagnetBoss(level, x, y, hx, hy),
  },
  {
    def: BASTION,
    create: (level, x, y, hx, hy) => new BastionBoss(level, x, y, hx, hy),
  },
  {
    def: STALKER,
    create: (level, x, y, hx, hy) => new StalkerBoss(level, x, y, hx, hy),
  },
  {
    def: SINGULARITY,
    create: (level, x, y, hx, hy) => new SingularityBoss(level, x, y, hx, hy),
  },
  {
    def: BROODWORM,
    create: (level, x, y, hx, hy) => new BroodwormBoss(level, x, y, hx, hy),
  },
  {
    def: WEBSPINNER,
    create: (level, x, y, hx, hy) => new WebspinnerBoss(level, x, y, hx, hy),
  },
  {
    def: WARDEN,
    create: (level, x, y, hx, hy) => new WardenBoss(level, x, y, hx, hy),
  },
];

/** Lookup for death-retry: the SAME boss returns after a defeat (docs/12 G1). */
export function bossById(id: string): BossEntry | undefined {
  return BOSS_POOL.find((e) => e.def.id === id);
}

/** Fresh endless rotation over the whole pool (docs/01 boss order). */
export function createRotation(): BossRotation {
  return new BossRotation(BOSS_POOL.map((e) => e.def));
}

/**
 * Rotation of one sector (docs/02 sector map): its regular pool + its
 * finale. Room gates (`minRoom`) are endless-only — the sector IS the gate.
 */
export function createSectorRotation(sector: number): BossRotation {
  const def = sectorAt(sector);
  const entry = (id: string, finale: boolean): RotationEntry => {
    const boss = bossById(id);
    if (!boss) throw new Error(`Sector ${def.id}: unknown boss "${id}"`);
    return { id, tier: boss.def.tier, finale };
  };
  const pool = [...def.bosses.map((id) => entry(id, false)), entry(def.finale, true)];
  return new BossRotation(pool, sectorRules(sector, pool));
}

/** Rotation factory for RunState: null = endless, else the sector index. */
export function rotationFor(sector: number | null): BossRotation {
  return sector === null ? createRotation() : createSectorRotation(sector);
}
